import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { useService } from '@xstate/react';

import { productBuilderMachine } from '../../../../context/ProductBuilder/machine';
import { getBaseProduct } from '../../../../helpers/getBaseProduct';
import { getAllowedValue } from '../../../../helpers/measurements';
import {
  getMaxHeight,
  getMaxWidth,
  getMinWidth,
} from '../../../../helpers/metafields';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useWindowSize } from '../../../../hooks/useWindowSize';
import { BackgroundImage } from './BackgroundImage';
import { BuilderMeasurement } from './BuilderMeasurement';
import { BuilderWindow } from './BuilderWindow';
import { BuilderWindowCurtainTransparency } from './BuilderWindowCurtainTransparency';
import { DriveControlImage } from './DriveControlImage';
import { ProductImage } from './ProductImage';
import { WindowBackground } from './WindowBackground';
import { ProductSwatchModal } from '../../ProductSwatchModal/ProductSwatchModal';
import { getStateKey } from '../../../../helpers/getStateKey';
import {
  getPatternImages,
  getRollerDayNightPatternImages,
} from '../../../../helpers/getPatternImages';
import { getDayColourSwatchUrl } from '../../../../helpers/getDayColourSwatchUrl';
import { getColourSample } from '../../../../helpers/getColourSample';
import { ZoomIcon } from '../../../Icon/ZoomIcon';

type Props = {
  children: ReactNode | ReactNode[];
};

const imageBase =
  process.env.NODE_ENV === 'development'
    ? '/images'
    : process.env.REACT_APP_IMAGE_BASE;

const Wrapper = styled.div`
  padding: 20px;
  min-height: 30vh;
  background-color: rgba(0, 0, 0, 0.1);
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  .previewer {
    position: absolute;
    width: 100%;
    height: 100%;
  }

  .layeredImages {
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    padding-bottom: 100px;
  }

  .stacked-image {
    position: absolute;
  }

  @media (min-width: 1024px) {
    width: 50%;
    height: 100%;
  }
  @media (max-width: 1023px) {
    min-height: 450px;
  }
  @media (max-width: 767px) {
    min-height: 385px;
  }
`;

const CurtainWrapper = styled.div`
  padding: 20px;
  min-height: 30vh;
  background-color: rgba(0, 0, 0, 0.1);
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex: 1;
  border-right: 5px solid #D9D9D9;


  .previewer {
    position: absolute;
    width: 100%;
    height: 100%;
  }

  .layeredImages {
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    padding-bottom: 100px;
  }

  .stacked-image {
    position: absolute;
  }

  @media (min-width: 768px) {
    width: 100%;
    height: 100%;
  }
@media (min-width: 991px) {
    height: 751px;
  }
    @media (min-width: 1951px) {
    height: 100%;
  }
@media (max-height: 650px) and (min-width: 991px) {
  height: 100%;    
}
  
`;

const StyledProductSwatch = styled.div<{
  activeColourSection: boolean;
}>`
  position: absolute;
  bottom: 25px;
  right: 25px;
  z-index: 1000;
  cursor: pointer;
  visibility: ${({ activeColourSection }) =>
    activeColourSection ? 'visible' : 'hidden'};

  img {
    box-shadow: 0px 3px 6px #00000029;
    border: 5px solid #ffffff;
    border-radius: 100%;
  }

  @media (max-width: 767px) {
    bottom: 10px;
    right: 15px;
  }

  @media (max-width: 768px) {
    img {
      max-width: 60px;
      max-height: 60px;
      border: 3px solid #ffffff;
    }
  }
`;

const StyledProductSwatchImage = styled.div`
  position: relative;
  svg {
    position: absolute;
    top: 0px;
    width: 30px;
    right: 3px;
  }
  @media (max-width: 768px) {
    svg {
      width: 20px;
      right: 0px;
    }
  }
`;

export const SectionImage = ({ children }: Props) => {
  const [isProductSwatchModal, setIsProductSwatchModal] = React.useState(false);
  const [state] = useService(productBuilderMachine);
  const setRef = React.useRef<SVGSVGElement>(null);
  const setProductImageRef = React.useRef<SVGSVGElement>(null);
  const stateKey = getStateKey(state.value);
  const activeColourSection =
    stateKey.includes('colour') || stateKey.includes('dayColour');
  const patternUrl = getPatternImages(state);
  const nightPatternUrl = getRollerDayNightPatternImages(state);
  const dayHoneycombPatternUrl = getDayColourSwatchUrl(state);
  let currentColourSwatch = stateKey.includes('colourNight')
    ? nightPatternUrl
    : stateKey.includes('dayColour')
    ? dayHoneycombPatternUrl
    : patternUrl;
  let currentColourName =
    typeof state.context.custom.colourNight !== 'undefined' &&
    stateKey === 'colourNight'
      ? state.context.custom.colourNight.split(': ')[1]
      : typeof state.context.custom.dayColour !== 'undefined' &&
        stateKey === 'dayColour'
      ? state.context.custom.dayColour.split(' (').shift() || ''
      : typeof state.context.custom.colourDay !== 'undefined' &&
        stateKey === 'colourDay'
      ? state.context.custom.colourDay.split(': ')[1]
      : typeof state.context.custom.colour !== 'undefined' &&
        stateKey === 'colour'
      ? state.context.custom.colour.split(': ')[1]
      : '';

  const colourSample = getColourSample(state.context, currentColourName);
  const colourSwatchImage =
    colourSample && colourSample.featured_image
      ? colourSample.featured_image.src
      : currentColourSwatch;

  const openProductSwatchModal = () => {
    setIsProductSwatchModal(true);
  };

  const closeProductSwatchModal = () => {
    setIsProductSwatchModal(false);
  };

  React.useEffect(() => {
    if (setRef?.current) {
    }
    if (setProductImageRef?.current) {
    }
  });

  const {
    custom: { measurements, transparency },
  } = state.context;

  const [productWidth, productHeight] = measurements
    ? (measurements as string).split('x').map(value => parseInt(value))
    : ['', ''];
  const baseProduct = getBaseProduct(state.context)
    ? getBaseProduct(state.context)
    : state.context.products[0];

  const minWidth = getMinWidth(baseProduct);
  const maxWidth = getMaxWidth(baseProduct);

  const maxHeight = getMaxHeight(
    baseProduct,
    getAllowedValue(productWidth, minWidth, maxWidth),
  );
  const windowSize = useWindowSize();
  const { width: windowWidth } = useDebounce(windowSize, 200);

  // Can fine tune this later.
  let width =
    windowWidth > 767
      ? windowWidth * 0.35
      : windowWidth > 540
      ? windowWidth * 0.55
      : windowWidth * 0.75;

  const fittingType = state.context.custom.fitting;
  const [windowZindex, productImageZindex] =
    fittingType === 'Reveal Fit' ? [3, 4] : [3, 4];

  const innerWindowWidth = setRef.current
    ? setRef.current.getBoundingClientRect().width
    : width;

  const fullWindowWidth =
    setRef.current && setRef.current.parentElement
      ? setRef.current.parentElement.getBoundingClientRect().width
      : width;

  const fullWindowHeight =
    setRef.current && setRef.current.parentElement
      ? setRef.current.parentElement.getBoundingClientRect().height
      : 'auto';

  const innerWindowHeight = setRef.current
    ? setRef.current.getBoundingClientRect().height
    : 'auto';

  const isCurtain = state.context.products[0].type === 'Curtain' ? true : false;

  // const outerWindowWidth = isCurtain === true ? width + 50 : width;

  let curtainMaxHeight = setRef.current
    ? setRef.current.getBoundingClientRect().height * 2
    : '100%';

  let curtainMinHeight = setRef.current
    ? setRef.current.getBoundingClientRect().height + 100
    : 'auto';

  const windowFrameWidth =
    setRef.current && setRef.current.parentElement
      ? setRef.current.parentElement.getBoundingClientRect().width -
        setRef.current.getBoundingClientRect().width
      : 0;

  const windowFrameHeight =
    setRef.current && setRef.current.parentElement
      ? setRef.current.parentElement.getBoundingClientRect().height -
        setRef.current.getBoundingClientRect().height
      : 0;

  const configuration = state.context.custom.configuration
    ? state.context.custom.configuration
    : 'Centre Opening (2 Curtains)';

  let arrowWidth =
    isCurtain === true &&
    configuration === 'Centre Opening (2 Curtains)' &&
    setRef.current &&
    setRef.current.parentElement
      ? setRef.current.parentElement.getBoundingClientRect().width + 50
      : isCurtain === true &&
        configuration !== 'Centre Opening (2 Curtains)' &&
        setRef.current &&
        setRef.current.parentElement
      ? setRef.current.parentElement.getBoundingClientRect().width + 75
      : setRef.current && setRef.current.parentElement
      ? setRef.current.parentElement.getBoundingClientRect().width
      : width;

  let arrowWidthInnerWindow =
    setRef.current && setRef.current.parentElement
      ? setRef.current.getBoundingClientRect().width
      : width;

  let arrowHeight =
    isCurtain === true && setProductImageRef.current
      ? setProductImageRef.current.getBoundingClientRect().height - 60
      : fittingType === 'Reveal Fit'
      ? innerWindowHeight
      : fullWindowHeight;

  const menuHeight = windowWidth > 1023 ? 129 : 115;
  let arrowTop =
    setRef.current && setRef.current.parentElement && isCurtain === false
      ? setRef.current.parentElement.getBoundingClientRect().top - menuHeight
      : fittingType === 'Reveal Fit' && isCurtain === false && setRef.current
      ? setRef.current.getBoundingClientRect().top - windowFrameHeight
      : isCurtain === true && setProductImageRef.current
      ? setProductImageRef.current.getBoundingClientRect().top - menuHeight
      : 'auto';

  let arrowRight =
    setRef.current && setRef.current.parentElement && isCurtain === false
      ? setRef.current.parentElement.getBoundingClientRect().right
      : fittingType === 'Reveal Fit' && isCurtain === false && setRef.current
      ? setRef.current.getBoundingClientRect().right - windowFrameWidth
      : isCurtain === true &&
        setRef.current &&
        setRef.current.parentElement &&
        configuration !== 'Left Stack (1 Curtain)'
      ? setRef.current.parentElement.getBoundingClientRect().right + 25
      : isCurtain === true &&
        setRef.current &&
        setRef.current.parentElement &&
        configuration === 'Left Stack (1 Curtain)'
      ? setRef.current.parentElement.getBoundingClientRect().right + 50
      : 'auto';

  let setProductImageRefBounding =
    setProductImageRef && setProductImageRef.current
      ? setProductImageRef.current.getBoundingClientRect()
      : '';

  const isSkylight =
    state.context.products[0].type === 'Honeycomb Skylight' ? true : false;

  return (
    isCurtain ?
      (
      <>
      <CurtainWrapper>
        {!isSkylight  && (
          <div className="previewer">
            <BackgroundImage
              zIndex={0}
              url={`${imageBase}/background.png?v=1234`}
            />
          </div>
        )}
        <div className="previewer layeredImages">
          <WindowBackground
            url={
              isSkylight === true
                ? `${imageBase}/builder-skylight-background.jpg?v=1234`
                : `${imageBase}/window.png`
            }
            className="stacked-image"
            style={{
              zIndex: 2,
              width: width,
               height :( isCurtain === true && windowWidth > 1951) ?  "calc(55%)": "auto",
               bottom :( isCurtain === true && windowWidth > 1951) ?  "calc(35%)": ""
            }}
          />
          {/* {typeof transparency !== 'undefined' && transparency !== 'Blockout' && (
            <div
              className="stacked-image"
              style={{
                width: setRef.current
                  ? setRef.current.getBoundingClientRect().width
                  : width,
                height: setRef.current
                  ? setRef.current.getBoundingClientRect().height
                  : 'auto',
                zIndex: 5,
              }}
            >
            </div>
            <BuilderWindowCurtainTransparency lining={transparency} />
          )} */}

          {/* <BuilderWindow
            className="stacked-image"
            style={{
              zIndex: windowZindex,
              width: width,
              height: 'auto',
            }}
            ref={setRef}
          /> */}
          <ProductImage
          style={{
            zIndex: productImageZindex,
            width:
              fittingType === 'Reveal Fit'
                ? innerWindowWidth
                : isCurtain === true
                  ? fullWindowWidth + 100
                  : fullWindowWidth,
            height:
              fittingType === 'Reveal Fit'
                ? innerWindowHeight
                : isCurtain === true
                  ? 'calc(77%)'
                  : fullWindowHeight,
            bottom: isCurtain === true ? 'calc(18%)' : 'auto',
            maxHeight: isCurtain === true ? curtainMaxHeight : 'auto',
            minHeight: isCurtain === true ? curtainMinHeight : 'auto',
          }}
          className="stacked-image"
          ref={setProductImageRef}
        />
        </div>
        {!isSkylight && windowWidth > 767 && (
          <div className="previewer" >
            <BackgroundImage
              zIndex={9}
              url={`${imageBase}/builder-foreground-curtain.png`}
            />
          </div>
        )}
      </CurtainWrapper>
      </>)
      : (<Wrapper>
        {!isSkylight && windowWidth > 767 && (
          <div className="previewer">
            <BackgroundImage
              zIndex={0}
              url={`${imageBase}/builder-background.jpg?v=1234`}
            />
          </div>
        )}
        <div className="previewer layeredImages">
          <BuilderMeasurement
            maxWidth={maxWidth}
            maxHeight={maxHeight}
            arrowWidth={
              fittingType === 'Reveal Fit' && isCurtain === false
                ? arrowWidthInnerWindow
                : arrowWidth
            }
            arrowHeight={arrowHeight}
            bottom={'auto'}
            top={isCurtain === false ? arrowTop : Number(arrowTop) + 24}
            arrowRight={arrowRight}
            windowFrameWidth={
              fittingType === 'Reveal Fit' && isCurtain === false
                ? windowFrameWidth
                : 0
            }
            windowFrameHeight={
              fittingType === 'Reveal Fit' && isCurtain === false
                ? windowFrameHeight
                : 0
            }
          />
          <DriveControlImage
            driveControlRight={arrowRight}
            windowHeight={fullWindowHeight}
            windowFrameHeight={
              fittingType === 'Reveal Fit' && isCurtain === false
                ? windowFrameHeight
                : 0
            }
            windowFrameWidth={
              fittingType === 'Reveal Fit' && isCurtain === false
                ? windowFrameWidth
                : 0
            }
            top={arrowTop}
            headRailPosition={setProductImageRefBounding}
            windowWidth={fullWindowWidth}
            menuHeight={menuHeight}
          />
          <WindowBackground
            url={
              isSkylight === true
                ? `${imageBase}/builder-skylight-background.jpg?v=1234`
                : `${imageBase}/builder-window-background.jpg?v=1234`
            }
            className="stacked-image"
            style={{
              zIndex: 2,
              width: width,
              height: 'auto',
            }}
          />
          {typeof transparency !== 'undefined' && transparency !== 'Blockout' && (
            <div
              className="stacked-image"
              style={{
                width: setRef.current
                  ? setRef.current.getBoundingClientRect().width
                  : width,
                height: setRef.current
                  ? setRef.current.getBoundingClientRect().height
                  : 'auto',
                zIndex: 5,
              }}
            >
              <BuilderWindowCurtainTransparency lining={transparency} />
            </div>
          )}

          <BuilderWindow
            className="stacked-image"
            style={{
              zIndex: windowZindex,
              width: width,
              height: 'auto',
            }}
            ref={setRef}
          />
          <ProductImage
            style={{
              zIndex: productImageZindex,
              width:
                fittingType === 'Reveal Fit'
                  ? innerWindowWidth
                  // : isCurtain === true
                  // ? fullWindowWidth + 100
                  : fullWindowWidth,
              height:
                fittingType === 'Reveal Fit'
                  // ? innerWindowHeight
                  // : isCurtain === true
                  ? 'auto'
                  : fullWindowHeight,
              bottom: 'auto',
              maxHeight: 'auto',
              minHeight: 'auto',
            }}
            className="stacked-image"
            ref={setProductImageRef}
          />
        </div>
        {!isSkylight && windowWidth > 767 && (
          <div className="previewer">
            <BackgroundImage
              zIndex={9}
              url={`${imageBase}/builder-foreground.png`}
            />
          </div>
        )}
        <StyledProductSwatch
          onClick={openProductSwatchModal}
          activeColourSection={activeColourSection}
        >
          <StyledProductSwatchImage>
            <img src={colourSwatchImage} alt='colourSwatchImage' width="112" height="112" />
            <ZoomIcon />
          </StyledProductSwatchImage>
        </StyledProductSwatch>
        <ProductSwatchModal
          modalIsOpen={isProductSwatchModal}
          openModal={openProductSwatchModal}
          closeModal={closeProductSwatchModal}
          colourSwatch={colourSwatchImage}
          colourName={currentColourName}
        />
      </Wrapper>)
  );
};
